import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { LoginPage } from "./routes/login";
import { HomePage } from "./routes/home";
import { legacyDesktopPath, legacyRailPath } from "./routes/home/railState";
import { GameSessionProvider } from "./lib/game/GameSessionProvider";
import { CompendiumProvider } from "./lib/CompendiumProvider";

// The root is just the app-wide session + compendium providers (+ the GM directive overlay the
// session renders) around the route Outlet; there is no global navbar and each route owns its own
// chrome. The CompendiumProvider runs the one IDB + world-pack merge for the app (it doesn't use
// the session, so order is free). Auth is not an inline gate over every route — it's the dedicated
// `/login` route plus per-route `RequireAuth` redirects.
const rootRoute = createRootRoute({
  component: () => (
    <GameSessionProvider>
      <CompendiumProvider>
        <Outlet />
      </CompendiumProvider>
    </GameSessionProvider>
  ),
});

// `/login` — the universal pre-auth screen (slot dropdown / GM host key).
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

// The post-login shell (`decisions/0203`): a pathless layout whose two children are `/` (the
// closed rail) and the `$` splat (every rail page — `/compendium/mechs/frame/mf_everest`,
// `/pilot/edit`, `/squad/<pilotId>`). The splat is the typed seam: `useRail` decodes `_splat` with
// `railState.ts`'s codec and writes it back through `navigate`. The shell mounts once in the
// layout, so moving between pages (or closing the rail) never remounts the stage or COMMS. The
// static `/login` route outranks the splat. There is no combat route: a fight is
// on the stage when the GM shows it, and a stale `/combat/<id>` link is an unknown page (the
// closed rail).
const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "shell",
  component: HomePage,
});

// `/` — the closed rail. A desktop-era link (`/?compendium=…`, `decisions/0019`) redirects to the
// rail page it meant.
const shellIndexRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/",
  beforeLoad: ({ search }) => {
    const legacy = legacyDesktopPath(search as Record<string, unknown>);
    if (legacy !== undefined) throw redirect({ to: "/$", params: { _splat: legacy }, replace: true });
  },
});

// `/$` — one rail page; the path after `/` is the page (see `railState.ts`). A retired page's path
// (`/forge/<id>`) redirects to the page that replaced it.
const shellPageRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "$",
  beforeLoad: ({ params }) => {
    const legacy = legacyRailPath((params as { _splat?: string })._splat ?? "");
    if (legacy !== undefined) throw redirect({ to: "/$", params: { _splat: legacy }, replace: true });
  },
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  shellRoute.addChildren([shellIndexRoute, shellPageRoute]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  // Module augmentation: TanStack Router declares `Register` as an `interface`;
  // merging into it requires `interface` (a `type` alias can't augment an
  // external module's interface via declaration merging). This is the one
  // sanctioned exception to the type-not-interface rule.
  // oxlint-disable-next-line typescript/consistent-type-definitions
  interface Register {
    router: typeof router;
  }
}
